/* 
 *  This file recruitment-module-independent.model.ts is part of HROne Inbox. The intelectual property owned by Uneecops Workplace Solution PVT. LTD.
 *  CopyrightYear: 2025
 *  (C) 2015-2025. All Right reserved. Uneecops Workplace Solution PVT. LTD.
 *  
 */

export type TRecruiterAccessSettingAPIResponse = {
    recruiterAccessMasterId: number;
    actionName: string;
    isAction: boolean;
    isView: boolean;
    sequenceNo: number;
}

export const actionNameToIdMap: Record<string, number> = {
    'Applicants': 1,
    'Candidate': 2,
    'Screening': 3,
    'Interview': 4,
    'Offer': 5,
    'Hire': 6,
    'Job details': 7,
    'Edit job opening': 46,
    'Change job status': 8,
    'Search candidates': 9,
    'Post on Referrals': 10,
    'Post on IJP': 11,
    'Post on naukri': 12,
    'Post on linkedin': 13,
    'Add candidate Button': 14,
    'Add candidate in bulk Button': 15,
    'Import candidate Button': 16,
    'Talent pool Button': 17,
    'Edit candidate': 52,
    'Add candidate from Chrome Extension': 53,
    'CV with hiring manager': 18,
    'Document completion': 19,
    'Profile completion': 20,
    'send eKYC': 21,
    'Send message': 22,
    'Add note': 23,
    'Schedule interview': 24,
    'Last 5 feedback': 25,
    'ViewGenerate offer letter': 26,
    'Share offer letter': 27,
    'Naukri': 28,
    'Linkedin': 29,
    'Post on Career portal': 30,
    'View Referral Card': 31,
    'View IJP Card': 32,
    'Add tags': 33,
    'Add reviews': 34,
    'Blacklist': 35,
    'Change sub stage': 36,
    'ViewDownload resume': 37,
    'Initiate assessment': 38,
    'Subscribe assessment': 39,
    'Add to position': 40,
    'Career page Link': 41,
    'Add cost': 42,
    'Upcoming join profile completion': 44,
    'Upcoming join': 45,
    'Edit workload': 47,
    'Change recruiter': 48,
    'Not joining': 51,
    'BGV': 50,
    'Edit new hire': 45,
    'New hire profile completion': 44,
};

export const defaultViewAccessIds = [1, 2, 3, 4, 5, 6];

// Chrome Web Store listing for the HROne recruit extension (LinkedIn / Naukri sourcing)
export const RECRUIT_EXTENSION_URL = 'https://chromewebstore.google.com/detail/recruit-extension-by-hron/plpocgmnoblaapehkbblhmoojnoboepf';
export const EXTENSION_ID = 'plpocgmnoblaapehkbblhmoojnoboepf';

export const RECRUIT_EXTENSION_PLATFORM_URLS: Record<'linkedin' | 'naukri', string> = {
    linkedin: 'https://www.linkedin.com',
    naukri: 'https://recruit.naukri.com'
};

export type TGeneralSettingAPIResponse = {
    settingId: number;
    settingName: string;
    status: string;
    settingValue: string;
    isActive: boolean;
    isExternal: boolean;
}

export type TGeneralSettingAPIPayload = {
    settingList: TGeneralSettingAPIResponse[];
}

export type TProvider = {
    providerId: string;
    providerName: string;
}

export type TProviderDetailAPIResponse = {
    ekycList: TProvider[];
    bgvList: TProvider[];
}

import {
    TDynamicDetail,
    TSocialDetail,
    TScreeningQuestionDetail
} from 'src/app/modules/recruitment/candidate/add-new-candidate/utilities/add-new-candidate-forAPI.model';

export type TCandidateWorkExperience = {
    experienceId: number;
    companyName: string;
    designation: string;
    fromDate: string;
    toDate: string;
    numberOfMonths: number;
    jobDescription: string;
    years: number;
    isCurrentWorking: boolean;
}

export type TCandidateQualification = {
    qualificationId: number;
    qualificationCode: string;
    qualificationName: string;
    specializationId: number;
    specializationName: string;
    qualificationMode: string;
    qualificationModeName: string;
    yearOfStartingDate: string;
    yearOfPassingDate: string;
    boardUniversityName: string;
    instituteName: string;
    grade: string;
    percentage: string;
    yearOfPassing: number;
}

export type TCandidateProfileResponse = {
    candidateId: number;
    candidateCode: string;
    requestId: string;
    currentPositionId: string;
    salutationId: number;
    firstName: string;
    middleName: string;
    lastName: string;
    dateOfBirth: string;
    genderId: number;
    nationality: string;
    personalEmail: string;
    mobileNo: string;
    addressCurrent: string;
    cityCodeCurrent: string;
    countryCodeCurrent: string;
    stateCodeCurrent: string;
    zipCodeCurrent: string;
    addressPermanent: string;
    cityCodePermanent: string;
    countryCodePermanent: string;
    stateCodePermanent: string;
    zipCodePermanent: string;
    preferLocation: string;
    candidateStatusId: number;
    uploadedFileName: string;
    uploadedFilePath: string;
    fileVirtualPath: string;
    joiningSource: number;
    joiningSourceCategory: number;
    recruiterId: number;
    skills: string;
    currentCostToCompany: number;
    expectedCostToCompany: number;
    noticePeriodType: string;
    noticePeriod: number;
    candidateRemarks: string;
    referredByEmployee: number;
    isCheckValidation: boolean;
    isShareProfileCompletion: boolean;
    isShareDocumentCompletion: boolean;
    isShareHiringManager: boolean;
    isFresher: boolean;
    profileFileName: string;
    profileFilePath: string;
    profileThumbnailFilePath: string;
    profileVirtualPath: string;
    naukriApplicantId: string;
    emergencyContactNo: string;
    emergencyContactName: string;
    contactRelationshipId: number;
    panNumber: string;
    uniqueIdentificationNumber: string;
    passportNo: string;
    passportExpiryDate: string;
    drivingLicenseNo: string;
    drivingLicenseExpiryDate: string;
    internationalCode: string;
    bloodGroupCode: string;
    referralId: number;
    vendorId: number;
    encryptedCandidateId: string;
    encryptedKycFor: string;
    workExperience: TCandidateWorkExperience[];
    qualification: TCandidateQualification[];
    dynamicDetails: TDynamicDetail[];
    socialDetails: TSocialDetail[];
    screeningQuestionDetails: TScreeningQuestionDetail[];
    experienceXml: string;
    qualificationXml: string;
    dynamicXml: string;
    socialXml: string;
    requestSource: string;
    screeningQuestionXml: string;
}